import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';

export const PAGE_SIZE = 20;

export function paginate(rows, page, size = PAGE_SIZE) {
  const start = (page - 1) * size;
  return rows.slice(start, start + size);
}

// Map caller-supplied label (e.g. "orders", "tickets") to the i18n key.
// Falls back to the raw string for anything we don't know about.
const labelKeyFor = (label) => {
  if (!label) return 'rows';
  const key = String(label).trim().toLowerCase();
  // The labels we ship localised in `common.json:pagination.*`. Anything
  // else (e.g. admin pages passing legacy strings) renders the raw label.
  if (['orders', 'transactions', 'tickets', 'customers', 'rows'].includes(key)) return key;
  return null;
};

export default function Pagination({ page, total, pageSize = PAGE_SIZE, onChange, label = 'rows', isAdmin = false }) {
  const { t } = useTranslation('common');
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (total === 0) return null;
  const go = (p) => onChange(Math.min(Math.max(1, p), pages));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  // Build page-number window: 1 … (current-1) current (current+1) … last
  const windowSize = 1;
  const numbers = [];
  for (let p = 1; p <= pages; p++) {
    if (p === 1 || p === pages || (p >= page - windowSize && p <= page + windowSize)) numbers.push(p);
    else if (numbers[numbers.length - 1] !== '…') numbers.push('…');
  }

  // Admin invariant: shared component must stay 100% English on admin pages.
  // Customer side: use the localised label + sentence with `<strong>` tags
  // preserved via i18next's HTML-as-template support.
  const localizedLabel = (() => {
    if (isAdmin) return label;
    const key = labelKeyFor(label);
    if (!key) return label;
    return t(`pagination.${key}`, { defaultValue: label });
  })();

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-slate-50 border-t border-slate-200" data-testid="pagination">
      <p className="text-sm text-slate-600">
        {isAdmin ? (
          <>
            Showing <span className="font-semibold text-slate-800">{from}–{to}</span> of <span className="font-semibold text-slate-800">{total.toLocaleString()}</span> {localizedLabel}
          </>
        ) : (
          <Trans
            i18nKey="pagination.showing"
            ns="common"
            values={{ from, to, total: total.toLocaleString(), label: localizedLabel }}
            components={{ strong: <span className="font-semibold text-slate-800" /> }}
          />
        )}
      </p>
      <div className="flex items-center gap-1">
        <button onClick={() => go(1)} disabled={page === 1} className="p-1.5 rounded hover:bg-white border border-slate-200 disabled:opacity-30" title={isAdmin ? 'First' : t('pagination.first', { defaultValue: 'First' })} data-testid="pg-first"><ChevronsLeft className="w-4 h-4" /></button>
        <button onClick={() => go(page - 1)} disabled={page === 1} className="p-1.5 rounded hover:bg-white border border-slate-200 disabled:opacity-30" title={isAdmin ? 'Previous' : t('pagination.previous', { defaultValue: 'Previous' })} data-testid="pg-prev"><ChevronLeft className="w-4 h-4" /></button>
        {numbers.map((n, i) => n === '…' ? (
          <span key={`e${i}`} className="px-1 text-slate-400">…</span>
        ) : (
          <button key={n} onClick={() => go(n)} data-testid={`pg-num-${n}`}
            className={`min-w-[34px] h-8 px-2 rounded text-sm font-medium border ${n === page ? 'bg-cyan-500 text-white border-cyan-500' : 'bg-white text-slate-700 border-slate-200 hover:bg-cyan-50'}`}>
            {n}
          </button>
        ))}
        <button onClick={() => go(page + 1)} disabled={page === pages} className="p-1.5 rounded hover:bg-white border border-slate-200 disabled:opacity-30" title={isAdmin ? 'Next' : t('pagination.next', { defaultValue: 'Next' })} data-testid="pg-next"><ChevronRight className="w-4 h-4" /></button>
        <button onClick={() => go(pages)} disabled={page === pages} className="p-1.5 rounded hover:bg-white border border-slate-200 disabled:opacity-30" title={isAdmin ? 'Last' : t('pagination.last', { defaultValue: 'Last' })} data-testid="pg-last"><ChevronsRight className="w-4 h-4" /></button>
      </div>
    </div>
  );
}
